import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  // Detect production by hostname — show prefilled demo creds only on preview / localhost
  const isProd = typeof window !== "undefined"
    && !/preview\.emergentagent\.com|localhost|127\.0\.0\.1/.test(window.location.hostname);
  const [email, setEmail] = useState(isProd ? "" : "admin@senrizeflow.com");
  const [password, setPassword] = useState(isProd ? "" : "Admin@12345");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/", { replace: true });
    } catch (err) {
      // Distinguish network/CORS errors from auth errors so the user knows what's wrong
      if (!err.response) {
        toast.error(err.message?.includes("timeout")
          ? "Server too slow to respond. Try again in a moment."
          : "Cannot reach the server. Check your internet, or contact support if this persists.");
      } else if (err.response.status === 401) {
        toast.error("Wrong email or password");
      } else if (err.response.status === 429) {
        toast.error("Too many login attempts. Please wait a few minutes.");
      } else if (err.response.status === 403) {
        toast.error("Your account is disabled. Contact an admin.");
      } else if (err.response.status >= 500) {
        toast.error(`Server error (${err.response.status}). Please try again or contact support.`);
      } else {
        const msg = formatApiError(err.response?.data?.detail);
        toast.error(
          msg && msg !== "Something went wrong."
            ? msg
            : `Login failed (HTTP ${err.response.status}). Try again or contact support.`
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background text-foreground">
      {/* Left visual */}
      <div className="hidden lg:flex flex-col justify-between p-10 border-r border-border relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div className="relative z-10 flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-foreground text-background flex items-center justify-center font-bold heading">S</div>
          <span className="heading text-2xl font-semibold tracking-tight">SenrizeFlow</span>
        </div>
        <div className="relative z-10 max-w-md space-y-5">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Agency Operations Platform</div>
          <h1 className="heading text-4xl xl:text-5xl font-semibold leading-tight tracking-tight">
            Run every client through one disciplined 7-day pipeline.
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Leads, content, design, video, approvals, payments — all in one workspace.
            Built for marketing agencies that ship.
          </p>
        </div>
        <div className="relative z-10 text-xs text-muted-foreground font-mono">
          v1.0 · Phase 1 MVP
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <Card className="w-full max-w-md p-8 border-border shadow-none">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-md bg-foreground text-background flex items-center justify-center font-bold heading">S</div>
            <span className="heading text-xl font-semibold">SenrizeFlow</span>
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Sign in</div>
          <h2 className="heading text-2xl font-semibold mb-1">Welcome back</h2>
          <p className="text-sm text-muted-foreground mb-6">Use your work email to access the agency workspace.</p>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider">Email</Label>
              <Input id="email" type="email" autoComplete="email" required value={email}
                     onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider">Password</Label>
              <div className="relative">
                <Input id="password" type={showPw ? "text" : "password"} autoComplete="current-password" required value={password}
                       onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="pr-10" />
                <button type="button" onClick={() => setShowPw(!showPw)}
                        className="absolute inset-y-0 right-0 px-3 flex items-center text-muted-foreground hover:text-foreground"
                        aria-label={showPw ? "Hide password" : "Show password"}
                        data-testid="login-show-password">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full bg-teal hover:bg-teal/90" disabled={loading} data-testid="login-submit">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
